import {
    Base,
    Clause,
    Contract,
    ContractSection,
    Coverage,
    Section,
    Segment,
    Subsection,
    Translation,
} from 'models';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun } from 'docx';

class ContractCreator {
    sectionMap: Map<string, Section>;
    subsectionMap: Map<string, Subsection>;
    coverageMap: Map<string, Coverage>;
    clauseMap: Map<string, Clause>;
    translationsMap?: Map<string, Translation>;
    constructor({
        sectionMap,
        subsectionMap,
        coverageMap,
        clauseMap,
        translationsMap,
    }: {
        sectionMap: Map<string, Section>;
        subsectionMap: Map<string, Subsection>;
        coverageMap: Map<string, Coverage>;
        clauseMap: Map<string, Clause>;
        translationsMap?: Map<string, Translation>;
    }) {
        this.sectionMap = sectionMap;
        this.subsectionMap = subsectionMap;
        this.coverageMap = coverageMap;
        this.clauseMap = clauseMap;
        this.translationsMap = translationsMap;
    }

    public text(record: Base): [string, string | undefined] {
        const text = record.__type === 'Clause' ? record.content : record.name;
        if (!this.translationsMap) {
            return [text, undefined];
        }
        const field = record.__type === 'Clause' ? 'content' : 'name';
        const translation = this.translationsMap.get(`${record.id}:${field}`);
        if (!translation) {
            return [text, 'ff0000'];
        }
        return [translation.content, undefined];
    }

    public header(contract: Contract): Paragraph[] {
        return [
            new Paragraph({
                children: [new TextRun({ text: contract.name, size: 40 })],
                spacing: { after: 128 },
            }),
        ];
    }

    public clause(
        clauseIndexId: string,
        { skipFirstBreak }: { skipFirstBreak?: boolean } = {}
    ): Paragraph[] {
        const clause = this.clauseMap.get(clauseIndexId);
        if (!clause) {
            throw new Error(`subsection ${clauseIndexId} not found`);
        }
        const [text, color] = this.text(clause);
        return [
            new Paragraph({
                children: text.split('\n').map((line, i) => {
                    return new TextRun({
                        text: line,
                        color,
                        size: 22,
                        break: !i && skipFirstBreak ? 0 : 1,
                    });
                }),
            }),
        ];
    }

    public subsection(subsectionIndexId: string): Paragraph[] {
        const subsection = this.subsectionMap.get(subsectionIndexId);
        if (!subsection) {
            throw new Error(`subsection ${subsectionIndexId} not found`);
        }
        const [text, color] = this.text(subsection);
        return [new Paragraph({ children: [new TextRun({ text, color, size: 28, break: 1 })] })];
    }

    public coverage(coverageIndexId: string): Paragraph[] {
        const coverage = this.coverageMap.get(coverageIndexId);
        if (!coverage) {
            throw new Error(`coverage ${coverageIndexId} not found`);
        }
        const [text, color] = this.text(coverage);
        return [new Paragraph({ children: [new TextRun({ text, color, size: 28, break: 1 })] })];
    }

    public segment(segment: Segment): Paragraph[] {
        return [
            ...(segment.subsectionIndexId ? this.subsection(segment.subsectionIndexId) : []),
            ...(segment.coverageIndexId ? this.coverage(segment.coverageIndexId) : []),
            ...(segment.clauseIndexId ? this.clause(segment.clauseIndexId) : []),
            ...segment
                .getClauses()
                .map((c, i, cs) => this.clause(c, { skipFirstBreak: i === 0 }))
                .reduce((accumulator, value) => accumulator.concat(value), []),
        ];
    }

    public section(contractSection: ContractSection): Paragraph[] {
        const section = this.sectionMap.get(contractSection.sectionIndexId);
        if (!section) {
            throw new Error(`section ${contractSection.sectionIndexId} not found`);
        }
        const [text, color] = this.text(section);
        return [
            new Paragraph({
                children: [new TextRun({ text, color, size: 32, break: 1 })],
                spacing: { before: 128, after: 128 },
            }),
            ...contractSection.segments
                .map((s) => this.segment(s))
                .reduce((accumulator, value) => accumulator.concat(value), []),
        ];
    }

    public footer(): Paragraph[] {
        return [];
    }

    public create(contract: Contract): Document {
        return new Document({
            sections: [
                {
                    children: [
                        ...this.header(contract),
                        ...contract.contractSections
                            .map((cs) => this.section(cs))
                            .reduce((accumulator, value) => accumulator.concat(value), []),
                        ...this.footer(),
                    ],
                },
            ],
        });
    }
}

export const wordContract = (
    contract: Contract,
    sectionMap: Map<string, Section>,
    subsectionMap: Map<string, Subsection>,
    coverageMap: Map<string, Coverage>,
    clauseMap: Map<string, Clause>,
    translationsMap?: Map<string, Translation>
) => {
    const documentCreator = new ContractCreator({
        sectionMap,
        subsectionMap,
        coverageMap,
        clauseMap,
        translationsMap,
    });
    const doc = documentCreator.create(contract);
    Packer.toBlob(doc).then((blob) => {
        saveAs(blob, `${contract.name.replace(/[\W_]+/g, '-')}.docx`);
        console.info('Document created successfully');
    });
};
